import * as palette from "../../components/symbols/palette"
import _ from "lodash"
import { getQuestionSubject } from "./createContentServices"
import suggestViews from "./schemas/suggest_views.json"
import { createResponse } from "./cloudFunctions" // your callable wrapper
import { GPT_5 } from "./chatGenerationServices"

const createSingleSuggestedView = async ({
  purpose,
  model = GPT_5,
  question, // { question: string, layer?: string, element_types?: string[] }
}) => {
  // Determine the "subject" for better element selection signal
  const subject = await getQuestionSubject({ question: question.question })
  console.log("%csubject", "color:lightgreen", { subject })

  // ---- Build prompt (rules-first to avoid clarifications/meta) ----
  const lines = [
    "SYSTEM RULES (follow exactly):",
    "- Return EXACTLY 1 item in next_best_views.",
    "- Output MUST be a single JSON object that validates against the provided schema. No extra text.",
    "",
    "** Context **",
    `- Overall objective: ${purpose}`,
    `- The question to answer: ${question.question}`,
    `- The main subject of the question: ${subject}`,
    "",
    "** Task **",
    "- Suggest a view that will best help answer the question.",
    "- Carefully select the best ArchiMate element type and layer for the view.",
    `- Only suggest 1 ArchiMate element type that best matches the main subject: ${subject}.`,
    "- Consider the meaning of each ArchiMate element as commonly defined (e.g., ArchiMate reference).",
  ]

  if (question.element_types?.length) {
    lines.push(
      "",
      `Candidate element types to consider for this question: ${question.element_types.join(
        ", "
      )}`
    )
  }

  const prompt = lines.join("\n")

  // ---- Build a runtime-modified schema from suggest_views.json ----
  // We want exactly one view back, and (optionally) restrict element_types enum to the layer
  const selectionParameters = _.cloneDeep(suggestViews.parameters)

  // Enforce exactly one view
  if (
    !selectionParameters?.properties?.next_best_views ||
    selectionParameters.properties.next_best_views.type !== "array"
  ) {
    console.error(
      "suggest_views.json must define properties.next_best_views as an array"
    )
    return null
  }
  selectionParameters.properties.next_best_views.minItems = 1
  selectionParameters.properties.next_best_views.maxItems = 1

  // If a layer was provided, constrain element_types.type enum to that layer’s ArchiMate types
  if (question.layer) {
    const elementTypesForLayer = palette
      .getElements()
      .filter((el) => el.layer.name === question.layer)
      .map((el) => el.name)

    // Path: next_best_views.items.properties.element_types.items.properties.type.enum
    const items =
      selectionParameters.properties.next_best_views.items ||
      (selectionParameters.properties.next_best_views.items = {
        type: "object",
        properties: {},
      })
    items.type = "object"
    items.properties = items.properties || {}
    const elTypes =
      items.properties.element_types ||
      (items.properties.element_types = {
        type: "array",
        items: { type: "object", properties: {} },
      })
    elTypes.type = "array"
    elTypes.items = elTypes.items || { type: "object", properties: {} }
    elTypes.items.type = "object"
    elTypes.items.properties = elTypes.items.properties || {}
    elTypes.items.properties.type = {
      type: "string",
      enum: elementTypesForLayer,
      description: `Allowed element types for layer "${question.layer}".`,
    }

    // Optional: force just one element_type to match your “only suggest 1 type” rule
    elTypes.minItems = 1
    elTypes.maxItems = 1
  }

  const jsonSchema = {
    name: suggestViews.name,
    description: suggestViews.description,
    parameters: selectionParameters,
  }

  // ---- Call Responses via your callable helper ----
  const result = await createResponse({
    prompt,
    model: "gpt-5-mini",
    reasoning: { effort: "low" }, // keeps it focused
    jsonSchema, // your helper maps to text.format
    store: true,
  })

  console.log("%cresponses result", "color:lightgreen", { result })

  // ---- Parse strict JSON output ----
  let suggestedView = null
  try {
    const outputText =
      result?.response?.output_text ?? result?.output_text ?? ""
    const parsed = JSON.parse(outputText)
    const views = parsed?.next_best_views || []
    suggestedView = views[0] || null
  } catch (e) {
    console.warn("Failed to parse suggested view JSON", e)
  }

  return suggestedView
}

export { createSingleSuggestedView }
